import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";

const RED = "#CE353C", CHAR = "#1D1D1B", HW = 120, COL = 150, LANE = 108, BW = 120, BH = 72, CH = 22;

const rounded = (pts, r = 6) => {
  let d = `M${pts[0][0]},${pts[0][1]}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const [px, py] = pts[i], [ax, ay] = pts[i - 1], [nx, ny] = pts[i + 1];
    const d1 = [Math.sign(px - ax), Math.sign(py - ay)], d2 = [Math.sign(nx - px), Math.sign(ny - py)];
    const rr = Math.min(r, (Math.abs(px - ax) + Math.abs(py - ay)) / 2, (Math.abs(nx - px) + Math.abs(ny - py)) / 2);
    d += ` L${px - d1[0] * rr},${py - d1[1] * rr} Q${px},${py} ${px + d2[0] * rr},${py + d2[1] * rr}`;
  }
  const l = pts[pts.length - 1];
  return `${d} L${l[0]},${l[1]}`;
};

function Pill({ x, y, text, color }) {
  const w = text.length * 5.6 + 14;
  return <g><rect x={x - w / 2} y={y - 8} width={w} height={16} rx="8" fill="hsl(var(--card))" stroke={color} strokeWidth="0.8" />
    <text x={x} y={y + 3.5} textAnchor="middle" fontSize="9.5" fontWeight="600" fill={color} fontFamily="Montserrat">{text}</text></g>;
}

function Step({ st, n, x, y, onClick, fid }) {
  const deco = st.kind === "decision" ? { stroke: RED, strokeDasharray: "4 3", fill: "#FFF6F6", strokeWidth: 1.4 } : st.kind === "auto" ? { stroke: CHAR, fill: "#F4F4F2", strokeWidth: 1.1 } : { stroke: RED, fill: "#FFFFFF", strokeWidth: 1.4 };
  return (
    <g onClick={onClick} className={st.link ? "cursor-pointer [&:hover>rect:first-child]:opacity-80" : ""} data-testid={`flow-${fid}-step-${st.id}`}>
      {st.link && <title>Open the guide for this step</title>}
      <rect x={x} y={y} width={BW} height={BH} rx="7" {...deco} />
      {st.kind === "decision" && <path d={`M${x + BW - 11},${y + 6} l5,5 l-5,5 l-5,-5 z`} fill={RED} />}
      <foreignObject x={x + 8} y={y + 6} width={BW - 16} height={BH - 8}>
        <div className="flex h-full flex-col items-center justify-center text-center leading-tight" style={{ fontFamily: "Montserrat" }}>
          <div className="text-[11.5px] font-semibold" style={{ color: CHAR }}>{st.label}</div>
          {st.sub && <div className="mt-0.5 text-[9.5px]" style={{ color: "#737373" }}>{st.sub}</div>}
        </div>
      </foreignObject>
      <circle cx={x + 2} cy={y + 2} r="9" fill={RED} stroke="#fff" strokeWidth="1.5" />
      <text x={x + 2} y={y + 5.5} textAnchor="middle" fontSize="10" fontWeight="700" fill="#fff" fontFamily="Montserrat">{n}</text>
    </g>
  );
}

function Legend() {
  const items = [["Step", <rect x="0" y="0" width="26" height="14" rx="3" fill="#fff" stroke={RED} strokeWidth="1.3" />], ["Decision", <rect x="0" y="0" width="26" height="14" rx="3" fill="#FFF6F6" stroke={RED} strokeWidth="1.3" strokeDasharray="3 2" />],
    ["Automatic check", <rect x="0" y="0" width="26" height="14" rx="3" fill="#F4F4F2" stroke={CHAR} strokeWidth="1" />], ["Next step", <path d="M0,7 H20 M20,3 L26,7 L20,11 z" stroke={CHAR} fill={CHAR} strokeWidth="1.2" />],
    ["Loop back", <path d="M0,7 H20" stroke={RED} strokeDasharray="3 2" strokeWidth="1.2" />]];
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t bg-muted/40 px-4 py-2.5 text-xs" data-testid="flow-legend">
      {items.map(([l, icon]) => <span key={l} className="flex items-center gap-2"><svg width="27" height="14" className="overflow-visible">{icon}{l === "Loop back" && <path d="M20,3 L26,7 L20,11 z" fill={RED} />}</svg>{l}</span>)}
      <span className="text-muted-foreground sm:ml-auto">Numbers show the order · click a step to open its guide</span>
    </div>
  );
}

export function FlowDiagram({ flow, lanes, testid }) {
  const navigate = useNavigate();
  const loops = flow.loops || [];
  const top = CH * loops.length + 22, n = flow.steps.length;
  const w = HW + COL * n, h = top + LANE * flow.lanes.length;
  const box = (j) => ({ x: HW + COL * j + (COL - BW) / 2, y: top + flow.lanes.indexOf(flow.steps[j].lane) * LANE + (LANE - BH) / 2 });
  const ids = flow.steps.map((s) => s.id);
  return (
    <div className="overflow-hidden rounded-xl border bg-card" data-testid={testid}>
      <div className="overflow-x-auto grid-scroll">
        <svg viewBox={`0 0 ${w} ${h + 2}`} width="100%" style={{ minWidth: w * 0.85 }} className="block" role="img" aria-label={`${flow.title} flow`}>
          {flow.lanes.map((ln, i) => (
            <g key={ln}>
              <rect x={HW} y={top + i * LANE} width={w - HW} height={LANE} className={i % 2 ? "fill-card" : "fill-muted/50"} />
              {i > 0 && <line x1={HW} x2={w} y1={top + i * LANE} y2={top + i * LANE} className="stroke-border" strokeWidth="1" />}
            </g>))}
          <rect x={0} y={top} width={HW} height={LANE * flow.lanes.length} fill={CHAR} />
          {flow.lanes.map((ln, i) => (
            <g key={ln}>{i > 0 && <line x1={10} x2={HW - 10} y1={top + i * LANE} y2={top + i * LANE} stroke="#4a4a48" />}
              <foreignObject x={10} y={top + i * LANE} width={HW - 20} height={LANE}><div className="flex h-full items-center justify-center text-center text-[12px] font-semibold leading-tight text-white" style={{ fontFamily: "Montserrat" }}>{lanes[ln]}</div></foreignObject></g>))}
          {flow.steps.slice(1).map((_, j) => {
            const a = box(j), b = box(j + 1), ay = a.y + BH / 2, by = b.y + BH / 2, gx = HW + COL * (j + 1);
            const pts = Math.abs(ay - by) < 1 ? [[a.x + BW, ay], [b.x - 7, by]] : [[a.x + BW, ay], [gx, ay], [gx, by], [b.x - 7, by]];
            const out = flow.steps[j].out;
            return <g key={j}><path d={rounded(pts)} fill="none" stroke={CHAR} strokeWidth="1.2" className="dark:stroke-white/80" /><path d={`M${b.x - 7},${by - 4} L${b.x},${by} L${b.x - 7},${by + 4} z`} className="fill-foreground" />
              {out && <Pill x={gx} y={Math.abs(ay - by) > 1 ? (ay + by) / 2 : a.y + BH + 9} text={out} color="#737373" />}</g>;
          })}
          {loops.map((lp, k) => {
            const fi = ids.indexOf(lp.from), ti = ids.indexOf(lp.to), f = box(fi), t = box(ti);
            const xl = HW + COL * fi + 7, xr = HW + COL * (ti + 1) - 7, sx = f.x + 22, ex = t.x + BW - 22, cy = 14 + CH * k;
            return <g key={k}><path d={rounded([[sx, f.y], [sx, f.y - 10], [xl, f.y - 10], [xl, cy], [xr, cy], [xr, t.y - 10], [ex, t.y - 10], [ex, t.y - 7]], 5)} fill="none" stroke={RED} strokeWidth="1.2" strokeDasharray="4 3" />
              <path d={`M${ex - 4},${t.y - 7} L${ex},${t.y} L${ex + 4},${t.y - 7} z`} fill={RED} /><Pill x={(xl + xr) / 2} y={Math.abs(xr - xl) < 50 ? cy - 9 : cy} text={lp.label} color={RED} /></g>;
          })}
          {(flow.ends || []).map((e) => {
            const j = ids.indexOf(e.from), b = box(j), nb = j + 1 < n ? box(j + 1) : null;
            const y = !nb || nb.y > b.y ? b.y + 13 : b.y + BH - 13, text = `End · ${e.label}`, pw = text.length * 6 + 16, cx = HW + COL * (j + 1) + COL / 2;
            return <g key={e.from} data-testid={`flow-${flow.id}-end-${e.from}`}><path d={`M${b.x + BW},${y} H${cx - pw / 2 - 7}`} stroke={RED} strokeWidth="1.2" strokeDasharray="4 3" />
              <path d={`M${cx - pw / 2 - 7},${y - 4} L${cx - pw / 2},${y} L${cx - pw / 2 - 7},${y + 4} z`} fill={RED} />
              <rect x={cx - pw / 2} y={y - 9} width={pw} height={18} rx="9" fill={RED} /><text x={cx} y={y + 3.5} textAnchor="middle" fontSize="10" fontWeight="600" fill="#fff" fontFamily="Montserrat">{text}</text></g>;
          })}
          {flow.steps.map((st, j) => { const b = box(j); return <Step key={st.id} fid={flow.id} st={st} n={j + 1} x={b.x} y={b.y} onClick={() => st.link && navigate(`/help/${st.link.split("#")[0]}#${st.link.replace("#", "-")}`)} />; })}
        </svg>
      </div>
      <Legend />
    </div>
  );
}

export function AuthImg({ name, alt, className, testid }) {
  const [src, setSrc] = useState(null);
  const [zoom, setZoom] = useState(false);
  useEffect(() => {
    let url;
    api.get(`/help/screens/${name}`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => setSrc(""));
    return () => url && URL.revokeObjectURL(url);
  }, [name]);
  if (src === "") return null;
  if (!src) return <div className={`${className} animate-pulse bg-muted`} style={{ minHeight: 160 }} />;
  return (
    <>
      <button type="button" onClick={() => setZoom(true)} className="block cursor-zoom-in text-left" aria-label={`Enlarge screenshot: ${alt}`} data-testid={`${testid}-zoom`}><img src={src} alt={alt} className={className} data-testid={testid} /></button>
      <Dialog open={zoom} onOpenChange={setZoom}>
        <DialogContent className="max-h-[94vh] max-w-[96vw] overflow-auto p-2 sm:max-w-[96vw]" data-testid={`${testid}-lightbox`}>
          <DialogTitle className="sr-only">{alt}</DialogTitle><img src={src} alt={alt} className="mx-auto h-auto max-w-none sm:max-w-full" />
        </DialogContent>
      </Dialog>
    </>
  );
}
